/**
 * PartnerDigestsCard — manage recurring per-partner inventory digests.
 *
 * Lists the org's partner digests (e.g. the weekly Heaps Good restock alert)
 * and opens a sheet to add / edit one. Mounted on the Notifications page.
 */
import { useState } from 'react';
import { Boxes, ChevronRight, Loader2, Plus } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PartnerDigestSheet, type PartnerDigestDraft } from './PartnerDigestSheet';

const WEEKDAY_LABEL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function cadenceSummary(d: PartnerDigestDraft): string {
  return d.cadence === 'weekly' ? `Weekly · ${WEEKDAY_LABEL[d.weeklyDay] ?? 'Monday'}` : 'Daily';
}

export function PartnerDigestsCard() {
  const listQuery = trpc.partnerDigests.list.useQuery();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<PartnerDigestDraft | null>(null);

  const openNew = () => {
    setEditing(null);
    setSheetOpen(true);
  };
  const openEdit = (d: PartnerDigestDraft) => {
    setEditing(d);
    setSheetOpen(true);
  };

  const digests = listQuery.data ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100">
            <Boxes className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base">Partner Inventory Digests</CardTitle>
            <CardDescription>
              Recurring Slack updates for partners you print for, surfacing only the chosen SKUs
              that are at or below their restock threshold.
            </CardDescription>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={openNew}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {listQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : digests.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No partner digests yet. Add one to post a weekly restock update to a partner's channel.
          </p>
        ) : (
          <div className="space-y-1">
            {digests.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => openEdit(d)}
                className="group flex w-full items-center gap-3 rounded-md px-2 py-2.5 -mx-2 text-left hover:bg-muted/40 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{d.label}</span>
                    {!d.enabled && (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">
                        Paused
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {cadenceSummary(d)} · {d.skuIds.length} SKU{d.skuIds.length === 1 ? '' : 's'}
                    {d.slackChannel ? ` · #${d.slackChannel.replace(/^#/, '')}` : ' · no channel'}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-foreground transition-colors shrink-0" />
              </button>
            ))}
          </div>
        )}
      </CardContent>

      <PartnerDigestSheet open={sheetOpen} onOpenChange={setSheetOpen} digest={editing} />
    </Card>
  );
}
